import { useState, useRef, useCallback, useEffect } from 'react';
import { usePositionPersistence } from './usePositionPersistence';

/**
 * Tidy animation UI state + position persistence + compact animation bridge refs.
 */
export function useFlowchartCoreTidyPersistence({
  tasks,
  autoPositions,
  campaignId,
  onPositionChange,
  onPositionChangeBatch,
  interaction,
  triggerLayout,
  triggerLayoutInstant,
  interactionAutoLayout,
  isMultiCampaignMode,
  nodeDimensions,
  elkPositionsOkRef,
  compactAnimationStartRef,
  compactAnimationEndRef,
}) {
  const [isAnimatingTidy, setIsAnimatingTidy] = useState(false);
  /** True only during partial-selection Tidy from persistence — not vertical-spacing / compact (those reuse `handleTidyAnimationStart` for canvas CSS). */
  const [isPartialTidyToolbarLoading, setIsPartialTidyToolbarLoading] = useState(false);
  const tidyAnimationTimeoutRef = useRef(null);

  const handleTidyAnimationStart = useCallback(() => {
    if (tidyAnimationTimeoutRef.current) {
      clearTimeout(tidyAnimationTimeoutRef.current);
    }
    setIsAnimatingTidy(true);
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker) blocker.style.display = 'block';
  }, []);

  const handleTidyAnimationEnd = useCallback(() => {
    tidyAnimationTimeoutRef.current = setTimeout(() => {
      setIsAnimatingTidy(false);
      setIsPartialTidyToolbarLoading(false);
      const blocker = document.querySelector('.flowchart__animation-blocker');
      if (blocker) blocker.style.display = '';
    }, 50);
  }, []);

  const handlePartialTidyPersistAnimationStart = useCallback(() => {
    handleTidyAnimationStart();
    setIsPartialTidyToolbarLoading(true);
  }, [handleTidyAnimationStart]);

  compactAnimationStartRef.current = handleTidyAnimationStart;
  compactAnimationEndRef.current = handleTidyAnimationEnd;

  const { handleAutoLayout, handleAutoLayoutInstant } = usePositionPersistence({
    tasks,
    autoPositions,
    campaignId,
    onPositionChange,
    onPositionChangeBatch,
    interaction,
    triggerLayout,
    triggerLayoutInstant,
    interactionAutoLayout,
    isMultiCampaignMode,
    nodeDimensions,
    onAnimationStart: handlePartialTidyPersistAnimationStart,
    onAnimationEnd: handleTidyAnimationEnd,
    elkPositionsOkRef,
  });

  useEffect(() => {
    setIsPartialTidyToolbarLoading(false);
  }, [campaignId]);

  return { handleAutoLayout, handleAutoLayoutInstant, isAnimatingTidy, isPartialTidyToolbarLoading };
}
